import { createContext, useContext, useEffect, useState } from "react";
import { setAuthToken, clearAuthToken } from "../utils/tokenStorage";
import config from "../config";
import { clearRefreshTokenExpiry } from "../utils/sessionExpiry";
import { checkAllPoliciesAcknowledged } from "../services/policyServices";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const localStorageRefreshToken = localStorage.getItem("refreshToken");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [policyStatus, setPolicyStatus] = useState({
    allAcknowledged: false,
    loading: false,
    lastCheck: null,
  });

  async function checkSession() {
    try {
      const response = await fetch(`${config.API_BASE_URL}auth/refresh`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          localStorageRefreshToken: localStorageRefreshToken,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setAuthToken(data.accessToken);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        throw new Error("Session invalid");
      }
    } catch (error) {
      console.warn("Session check failed:", error.message);
      clearAuthToken();
      clearRefreshTokenExpiry();
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }

  const checkPolicyAcknowledgment = async (employeeId) => {
    if (!employeeId || !isAuthenticated) return;

    setPolicyStatus(prev => ({ ...prev, loading: true }));
    
    try {
      const result = await checkAllPoliciesAcknowledged(employeeId);
      setPolicyStatus({
        allAcknowledged: result.allAcknowledged,
        loading: false,
        lastCheck: new Date().toISOString(),
        unacknowledgedCount: result.unacknowledgedPolicies?.length || 0,
        totalPolicies: result.totalPolicies || 0,
      });
    } catch (error) {
      console.error("Error checking policy status:", error);
      setPolicyStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const refreshPolicyStatus = async () => {
    const employeeId = localStorage.getItem("employeeID");
    if (employeeId) {
      await checkPolicyAcknowledgment(employeeId);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  // Check policy status after authentication is confirmed
  useEffect(() => {
    if (isAuthenticated) {
      const employeeId = localStorage.getItem("employeeID");
      if (employeeId) {
        checkPolicyAcknowledgment(employeeId);
      }
    }
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      setIsAuthenticated, 
      policyStatus,
      refreshPolicyStatus,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
